import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../components/snackbar";
import { useStudentStore } from "../store/student-store";
import axios from "axios";
import { Classroom } from "../models/classroom";
import { Student } from "../models/student";
import { Assessment } from "../models/assessments";
import { Collection } from "../models/collection";
import { Resource } from "../models/resource";
import Lecture from "../models/lecture";
import { Dashboard } from "../models/dashboard";

const API_URL = process.env.REACT_APP_API_URL;

export const useStudentActions = () => {
    const { showMessage } = useSnackbar();

    const navigate = useNavigate();

    const { 
        student, 
        setStudent, 
        classrooms, 
        setClassrooms,
        dashboard,
        setDashboard
    } = useStudentStore();

    const getStudent = async () => {
        try {
            const response = await axios.get<Student>(
                `${API_URL}/student`,
                { withCredentials: true }
            );
            
            setStudent({
                ...response.data,
                password: "",
                password_repeat: ""
            });
            const { name, email } = response.data;
            localStorage.setItem("student", JSON.stringify({ name, email }));
        } catch (error) {        
            handleApiError(error);
        }
    };

    const updateStudent = async (payload: any, action: number) => {
        try {
            const response = await axios.put<Student>(
                `${API_URL}/student/${action === 0 ? 'profile' : 'password'}`,
                payload,
                { withCredentials: true }
            );

            if (action === 0) {
                setStudent({
                    id: student?.id ?? 0,
                    name: student?.name ?? '',
                    email: student?.email ?? '',
                    password: student?.password ?? '',
                    password_repeat: student?.password_repeat ?? '',
                    country: student?.country ?? '',
                    phone_number: response.data.phone_number,
                    state: response.data.state,
                    lga: response.data.lga
                });

                showMessage('Profile Updated', 3);
            } else {
                setStudent({
                    id: student?.id ?? 0,
                    name: student?.name ?? '',
                    email: student?.email ?? '',
                    password: '',
                    password_repeat: '',
                    country: student?.country ?? '',
                    phone_number: student?.phone_number ?? '',
                    state: student?.state ?? 0,
                    lga: student?.lga  ?? 0
                });
                showMessage('Password Updated', 3);
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const getClassrooms = async () => {
        try {
            const response = await axios.get<Classroom[]>(
                `${API_URL}/student/classrooms`,
                { withCredentials: true }
            );
            
            const classroomsMap = response.data.reduce((acc, cls) => {
                acc[cls.id] = cls;
                return acc;
            }, {} as Record<number, Classroom>);
     
            
            setClassrooms(classroomsMap);
        } catch (error) {        
            handleApiError(error);
        }
    };

    const unEnroll = async (classroom_id: number) => {
        try {
            const response = await axios.delete(
                `${API_URL}/student/classrooms/${classroom_id}`,
                { withCredentials: true }
            );

            const _classrooms = classrooms || {};
            
            const { [classroom_id]: _, ...updatedClassrooms } = _classrooms;

            setClassrooms(updatedClassrooms);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateRegNumber = async (classroom_id: number, regNumber: string) => {
        try {
            const response = await axios.put<Classroom>(
                `${API_URL}/student/classrooms/${classroom_id}/reg-number`,
                {},
                {
                    params: { reg_number: regNumber },
                    withCredentials: true
                }
            );

            if (response.data) {
                const _classrooms = classrooms || {};

                setClassrooms({
                    ..._classrooms,
                    [classroom_id]: {
                        ..._classrooms[classroom_id],
                        reg_number: regNumber
                    }
                });

                showMessage("Updated Reg. Number", 3);
            }

        } catch (error) {
            handleApiError(error);
        }
    };

    const getDashboard = async () => {
        try {
            const response = await axios.get<Dashboard>(`${API_URL}/student/dashboard`,
                { withCredentials: true }
            );

            setDashboard(response.data);
        } catch (error) {
            handleApiError(error);
        }
    }

    const getAssessments = async (classroom_id: number) => {
        try {
            const response = await axios.get<Assessment[]>(`${API_URL}/student/classrooms/${classroom_id}/assessments`,
                { withCredentials: true }
            );
            
            const assessmentsMap = response.data.reduce((acc, ass) => {
                acc[ass.id] = ass;
                return acc;
            }, {} as Record<number, Assessment>);

            const _classrooms = classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...(_classrooms[classroom_id] || {}),
                    assessments: assessmentsMap
                }
            });

        } catch (error) {
            handleApiError(error);
        }
    };

    const getAssessment = async (classroom_id: number, assessment_id: number) => {
        try {
            const response = await axios.get<Assessment>(
                `${API_URL}/student/classrooms/${classroom_id}/assessments/${assessment_id}`,
                { withCredentials: true }
            );

            const _classrooms = classrooms || {};

        
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...(_classrooms[classroom_id] || {}),
                    assessments: {
                        ...(_classrooms[classroom_id]?.assessments || {}),
                        [assessment_id]: response.data
                    }
                }
            });
            
        } catch (error) {
            handleApiError(error);
        }
    };

    const startAssessment = async (classroom_id: number, assessment_id: number) => {
        type ResponseData = {
            started_at: string,
            questions: []
        };

        try {
            const response = await axios.put<ResponseData>(
                `${API_URL}/student/classrooms/${classroom_id}/assessments/${assessment_id}/start`,
                {},
                { withCredentials: true }
            );

            const _classrooms = classrooms || {};
            const classroom = _classrooms[classroom_id] || {};
            const assessments = classroom.assessments || {};
            const assessment = assessments[assessment_id] || {};

        
            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...classroom,
                    assessments: {
                        ...assessments,
                        [assessment_id]: {
                            ...assessment,
                            started_at: response.data.started_at,
                        }
                    }
                }
            });
            
            if (response.data) return response.data.questions; 
        } catch (error) {
            handleApiError(error);
        }
    }

    const submitAssessment = (classroom_id: number, assessment_id: number) => {
        // update in classrooms

        const _classrooms = classrooms || {};
        const classroom = _classrooms[classroom_id] || {};
        const assessments = classroom.assessments || {};
        const assessment = assessments[assessment_id] || {};

        setClassrooms({
            ..._classrooms,
            [classroom_id]: {
                ...classroom,
                assessments: {
                    ...assessments,
                    [assessment_id]: {
                        ...assessment,
                        submitted: true
                    }
                }
            }
        });

        // update in dashboard

        if (dashboard) {
            const updatedAssessments = dashboard.assessments.map((a) =>
                a.id === assessment_id ? { ...a, submitted: true } : a
            );

            setDashboard({
                ...dashboard,
                assessments: updatedAssessments
            });
        }
        
    }

    const getCollections = async (classroom_id: number) => {
        try {
            const response = await axios.get<Collection[]>(`${API_URL}/student/classrooms/${classroom_id}/collections`,
                { withCredentials: true }
            );
            
            const lecturesMap = response.data.reduce((acc, col) => {
                acc[col.id] = col;
                return acc;
            }, {} as Record<number, Collection>);

            const _classrooms = classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...(_classrooms[classroom_id] || {}),
                    collections: lecturesMap
                }
            });

        } catch (error) {
            handleApiError(error);
        }
    };

    const getCollection = async (classroom_id: number, collection_id: number) => {
        try {
            const response = await axios.get<Resource[]>(
                `${API_URL}/student/classrooms/${classroom_id}/collections/${collection_id}`,
                { withCredentials: true }
            );
            
            const _classrooms = classrooms || {};

        
            const currentClassroom = _classrooms[classroom_id] || {};
            const currentCollections = currentClassroom.collections || {};
            const currentCollection = currentCollections[collection_id] || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...currentClassroom,
                    collections: {
                        ...currentCollections,
                        [collection_id]: {
                            ...currentCollection,
                            resources: response.data,
                        },
                    },
                },
            });

            
        } catch (error) {
            handleApiError(error);
        }
    };
    
    const getLectures = async (classroom_id: number) => {
        try {
            const response = await axios.get<Lecture[]>(`${API_URL}/student/classrooms/${classroom_id}/lectures`,
                { withCredentials: true }
            );
            
            const lecturesMap = response.data.reduce((acc, lec) => {
                acc[lec.id] = lec;
                return acc;
            }, {} as Record<number, Lecture>);

            const _classrooms = classrooms || {};

            setClassrooms({
                ..._classrooms,
                [classroom_id]: {
                    ...(_classrooms[classroom_id] || {}),
                    lectures: lecturesMap
                }
            });

        } catch (error) {
            handleApiError(error);
        }
    }

    const logOut = async () => {
        try {
            await axios.delete(
                `${API_URL}/auth/students/logout`,
                { withCredentials: true }
            );
            localStorage.clear();
            
            window.location.href = "/sign-in";
        } catch (error: any) {
            handleApiError(error);
        }
    };


    const handleApiError = (error: any) => {
        if (error?.response?.status === 401) {
            navigate("/sign-in");
            return;
        }

        const message =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";

        showMessage(message, 0);
    };

    

    return {
        student,
        classrooms,
        getStudent,
        updateStudent,
        getClassrooms,
        updateRegNumber,
        dashboard,
        getDashboard,
        getLectures,
        getAssessments,
        startAssessment,
        submitAssessment,
        getAssessment,
        getCollections,
        getCollection,
        unEnroll,
        logOut
    };

};